// Maps InsightActionCard + parent InsightCard into a unified ActionWorkflowDefinition.
// Add a new entry to STEPS_BY_KIND when you introduce a new InsightActionKind.

import type {
    InsightActionCard,
    InsightActionKind,
    InsightCard
} from "../../insightCardModels";
import {getAgentForCard, toAgentIdentity} from "../../insightAgents";
import type {AgentIdentity} from "../../insightAgents";
import type {ActionWorkflowDefinition, WorkflowEditableField, WorkflowStep} from "./workflowModels";
import {getCampaignAction} from "./campaignActions";
import {opensLaborSchedule} from "./laborScheduleActions";
import {opensVoidRefundReport} from "./voidRefundReportActions";
import {DECISION_SUPPORT_BY_ACTION_ID} from "./decisionSupport";
import type {CampaignActionType} from "./campaignActions";
import type {DiagnosisReport} from "./intelligenceModels";

const STEPS_BY_KIND: Record<InsightActionKind, WorkflowStep[]> = {
    campaign: [
        {
            id: "1",
            label: "Build audience",
            detail: "Target segment identified from your data"
        },
        {
            id: "2",
            label: "Configure offer & message",
            detail: "Set reward, copy, and delivery channel"
        },
        {
            id: "3",
            label: "Schedule launch",
            detail: "Campaign goes live on the chosen timeline"
        },
        {
            id: "4",
            label: "Track lift",
            detail: "Results reported after the campaign window closes"
        }
    ],
    automation: [
        {
            id: "1",
            label: "Define trigger condition",
            detail: "The event or threshold that fires this automation"
        },
        {
            id: "2",
            label: "Configure action chain",
            detail: "Steps that execute when the trigger fires"
        },
        {
            id: "3",
            label: "Activate",
            detail: "Automation runs silently in the background"
        }
    ],
    settings: [
        {
            id: "1",
            label: "Review current config",
            detail: "Snapshot of existing settings for selected locations"
        },
        {
            id: "2",
            label: "Apply changes",
            detail: "New settings pushed to selected stores"
        },
        {
            id: "3",
            label: "Confirm & log",
            detail: "Change confirmed and recorded in audit trail"
        }
    ],
    analysis: [
        {id: "1", label: "Pull data", detail: "Last 30 days across selected scope"},
        {
            id: "2",
            label: "Run analysis",
            detail: "Statistical model applied to your data"
        },
        {
            id: "3",
            label: "Generate findings",
            detail: "Results ready in the Reports tab"
        }
    ],
    report: [
        {
            id: "1",
            label: "Scope selection",
            detail: "Locations, date range, and metrics confirmed"
        },
        {
            id: "2",
            label: "Build report",
            detail: "Data assembled from all relevant sources"
        },
        {
            id: "3",
            label: "Deliver",
            detail: "Available in Reports and optionally emailed to recipients"
        }
    ],
    cogs: [
        {
            id: "1",
            label: "Identify variances",
            detail: "Items outside target cost range flagged"
        },
        {
            id: "2",
            label: "Classify causes",
            detail: "Waste, theft, or measurement error categorized"
        },
        {
            id: "3",
            label: "Generate correction plan",
            detail: "Prioritized fix list created for your team"
        }
    ],
    ai_task: [
        {
            id: "1",
            label: "Gather context",
            detail: "Relevant data pulled from your locations"
        },
        {
            id: "2",
            label: "Run AI model",
            detail: "PAR Intelligence analyzes the data"
        },
        {
            id: "3",
            label: "Return findings",
            detail: "Results surfaced in this conversation"
        }
    ],
    ops_task: [
        {
            id: "1",
            label: "Define scope",
            detail: "Locations and responsible parties confirmed"
        },
        {
            id: "2",
            label: "Create task list",
            detail: "Step-by-step action items generated"
        },
        {
            id: "3",
            label: "Assign & track",
            detail: "Owners notified, progress tracked"
        }
    ],
    alert: [
        {
            id: "1",
            label: "Set threshold",
            detail: "Trigger condition for the alert defined"
        },
        {id: "2", label: "Choose recipients", detail: "Who gets notified and how"},
        {
            id: "3",
            label: "Activate monitoring",
            detail: "Alert fires when threshold is crossed"
        }
    ],
    comms: [
        {
            id: "1",
            label: "Draft message",
            detail: "Content prepared from insight context"
        },
        {
            id: "2",
            label: "Select recipients",
            detail: "Team, location group, or individuals"
        },
        {
            id: "3",
            label: "Send or schedule",
            detail: "Delivered via Slack, email, or both"
        }
    ],
    pos: [
        {
            id: "1",
            label: "Identify change",
            detail: "POS configuration to add or modify"
        },
        {
            id: "2",
            label: "Apply to locations",
            detail: "Change pushed to selected stores"
        },
        {id: "3", label: "Verify", detail: "Configuration confirmed on POS"}
    ],
    schedule: [
        {
            id: "1",
            label: "Analyze demand",
            detail: "Traffic and coverage data reviewed"
        },
        {
            id: "2",
            label: "Build schedule change",
            detail: "Proposed adjustments generated"
        },
        {
            id: "3",
            label: "Submit for approval",
            detail: "Managers review proposed changes in scheduler"
        }
    ],
    labor: [
        {
            id: "1",
            label: "Review staffing model",
            detail: "Current vs. needed coverage analyzed"
        },
        {
            id: "2",
            label: "Identify adjustments",
            detail: "Specific shift changes recommended"
        },
        {
            id: "3",
            label: "Notify managers",
            detail: "GMs informed of proposed changes"
        }
    ],
    coaching: [
        {
            id: "1",
            label: "Identify coaching targets",
            detail: "Employees or locations to focus on"
        },
        {
            id: "2",
            label: "Prepare materials",
            detail: "Scripts, data, and talking points generated"
        },
        {
            id: "3",
            label: "Schedule follow-up",
            detail: "Check-in reminder set after the coaching session"
        }
    ],
    checklist: [
        {
            id: "1",
            label: "Load template",
            detail: "Portioning audit steps pre-populated from recipe standards"
        },
        {
            id: "2",
            label: "Assign team",
            detail: "Steps distributed to shift leads and prep staff"
        },
        {
            id: "3",
            label: "Execute audit",
            detail: "Team completes checklist during next prep period"
        },
        {
            id: "4",
            label: "Log findings",
            detail: "Variances recorded and submitted for manager sign-off"
        }
    ]
};

// Deterministic fallback steps that differ per campaign operation. Every campaign
// action shares kind "campaign"/"settings", so keying steps by kind alone made
// "Retarget Segment", "Swap Offer", and "Delete" read identically. These give
// each operation a distinct plan even before the LLM refines it (and if it fails).
const STEPS_BY_CAMPAIGN_ACTION: Record<CampaignActionType, WorkflowStep[]> = {
    edit_segment: [
        {
            id: "1",
            label: "Review current audience",
            detail: "See who the campaign reaches today and why the rate is low"
        },
        {
            id: "2",
            label: "Pick a new segment",
            detail: "Choose a narrower, higher-intent audience on the next screen"
        },
        {
            id: "3",
            label: "Confirm & send",
            detail: "Review the full campaign, then send to the new segment"
        }
    ],
    edit_promo: [
        {
            id: "1",
            label: "Review current offer",
            detail: "See the gift type in market today and its redemption rate"
        },
        {
            id: "2",
            label: "Pick a new gift type",
            detail: "Swap in a gift type with a stronger draw on the next screen"
        },
        {
            id: "3",
            label: "Confirm & send",
            detail: "Review the full campaign, then send with the new offer"
        }
    ],
    delete: [
        {
            id: "1",
            label: "Review the campaign",
            detail: "Confirm the under-performing campaign you're removing"
        },
        {
            id: "2",
            label: "Confirm delete",
            detail: "Approve the deletion on the confirmation screen"
        },
        {
            id: "3",
            label: "Sends stop",
            detail: "The campaign is removed and discount spend stops immediately"
        }
    ],
    create: [
        {
            id: "1",
            label: "Define the audience",
            detail: "Target the at-risk / win-back segment for the new campaign"
        },
        {
            id: "2",
            label: "Configure the offer",
            detail: "Set the win-back gift type, name, and start time"
        },
        {
            id: "3",
            label: "Schedule & launch",
            detail: "Review the full campaign, then send it live"
        }
    ]
};

export function buildWorkflowFromInsightAction(
    action: InsightActionCard,
    card: InsightCard,
    agent?: AgentIdentity
): ActionWorkflowDefinition {
    const decisionConfig = DECISION_SUPPORT_BY_ACTION_ID[action.id];

    const bulletFields: WorkflowEditableField[] = (action.workflow.bullets ?? [])
        .filter((bullet) => bullet.label !== decisionConfig?.replacesBulletLabel)
        .map((bullet, index) => ({
            id: `field_${index}`,
            label: bullet.label,
            type: "text" as const,
            value: bullet.value
        }));

    // Decision-support actions get a hidden select field, empty until candidates
    // load in IntelligenceWorkflowView. It carries the user's candidate choice
    // through the chat-modify round trip and into the confirm payload, but is
    // never rendered directly — DecisionCandidateCards is the UI for it.
    const editableFields: WorkflowEditableField[] = decisionConfig
        ? [
              ...bulletFields,
              {
                  id: decisionConfig.fieldId,
                  label: decisionConfig.fieldLabel,
                  type: "select" as const,
                  value: "",
                  options: []
              }
          ]
        : bulletFields;

    const resolvedAgent =
        agent ??
        (() => {
            const owner = getAgentForCard(card);
            return owner ? toAgentIdentity(owner) : undefined;
        })();

    const campaignAction = getCampaignAction(action.id);
    const steps =
        (campaignAction && STEPS_BY_CAMPAIGN_ACTION[campaignAction]) ??
        STEPS_BY_KIND[action.kind];

    return {
        id: `wf_${action.id}`,
        actionId: action.id,
        kind: action.kind,
        title: action.title,
        category: action.category,
        intent: action.workflow.intro,
        impactSummary: `${card.headlineMetric.value} \u00b7 ${card.headlineMetric.label}`,
        steps,
        editableFields,
        ctaLabel: action.ctaLabel,
        successMessage:
            action.workflow.followUp ??
            `${action.ctaLabel} is underway. You'll receive a confirmation once complete.`,
        sourceInsightTitle: card.title,
        sourceInsightId: card.id,
        sourceInsightNarrative: card.narrative,
        sourceInsightAiNote: card.aiNote,
        sourceHeadlineMetricValue: card.headlineMetric.value,
        sourceHeadlineMetricLabel: card.headlineMetric.label,
        actionDescription: action.description,
        agent: resolvedAgent,
        opensLaborSchedule: opensLaborSchedule(action.id),
        opensVoidRefundReport: opensVoidRefundReport(action.id),
        reportContext: action.reportContext,
        campaignAction,
        campaignId: card.campaignId
    };
}

export function buildWorkflowFromDiagnosis(
    report: DiagnosisReport
): ActionWorkflowDefinition {
    const topKpi = report.kpis[0];
    const impactSummary = topKpi
        ? `${topKpi.value} \u00b7 ${topKpi.label}`
        : undefined;

    return {
        id: `wf_diag_${Date.now()}`,
        kind: "ops_task",
        title: `Action plan: ${report.title}`,
        category: "Investigation",
        intent: report.key_finding,
        impactSummary,
        steps: STEPS_BY_KIND["ops_task"],
        editableFields: [
            {
                id: "assignee",
                label: "Assign to",
                type: "text" as const,
                value: report.follow_up_with,
                hint: "Role or person responsible for follow-through"
            },
            {
                id: "time_period",
                label: "Time period",
                type: "text" as const,
                value: report.time_period
            }
        ],
        ctaLabel: "Create action plan",
        successMessage:
            "Action plan created. The assigned team will be notified to follow up.",
        sourceInsightTitle: report.title
    };
}
